import { useState, useEffect } from 'react';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';
import Alert from '@cloudscape-design/components/alert';
import { api, type MailManagerRule } from '../api';
import { useTenant } from '../tenant/TenantContext';
import RuleFormDrawer from '../components/RuleFormDrawer';

function parseAction(rule: MailManagerRule): { type: 'forward' | 'drop'; destination?: string } {
  const action = rule.Actions?.[0];
  if (action?.Drop) return { type: 'drop' };
  if (action?.ReplaceRecipient) return { type: 'forward', destination: action.ReplaceRecipient.ReplaceWith?.[0] };
  return { type: 'drop' };
}

function parseCondition(rule: MailManagerRule): string {
  const cond = rule.Conditions?.[0]?.StringExpression;
  if (!cond) return '(no condition)';
  const attr = cond.Evaluate?.Attribute || '?';
  const op = cond.Operator || 'CONTAINS';
  const val = cond.Values?.[0] || '';
  return `${attr} ${op.toLowerCase()} "${val}"`;
}

/**
 * Reverse of the Rule Engine Lambda's translation: map a Mail Manager rule back
 * to the friendly {field, operator, value} form the edit form works with.
 */
const ATTR_TO_FIELD: Record<string, string> = {
  FROM: 'from',
  SENDER: 'sender',
  SUBJECT: 'subject',
  RECIPIENT: 'recipient',
  TO: 'to',
};

const OP_TO_OPERATOR: Record<string, string> = {
  CONTAINS: 'contains',
  EQUALS: 'equals',
  NOT_EQUALS: 'not-equals',
  STARTS_WITH: 'starts-with',
  ENDS_WITH: 'ends-with',
};

interface EditingRule {
  ruleId: string;
  condition: { field: string; operator: string; value: string };
  action: { type: string; forwardTo?: string };
}

function toEditingRule(rule: MailManagerRule): EditingRule {
  const cond = rule.Conditions?.[0]?.StringExpression;
  const action = parseAction(rule);
  return {
    ruleId: rule.Name,
    condition: {
      field: ATTR_TO_FIELD[cond?.Evaluate?.Attribute ?? ''] ?? 'from',
      operator: OP_TO_OPERATOR[cond?.Operator ?? ''] ?? 'contains',
      value: cond?.Values?.[0] ?? '',
    },
    action: { type: action.type, forwardTo: action.destination },
  };
}

export default function RulesView() {
  const [rules, setRules] = useState<MailManagerRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<EditingRule | undefined>(undefined);
  const [error, setError] = useState('');
  const { tenant } = useTenant();

  // Rules are per-tenant: reload when the selected mailbox changes.
  useEffect(() => {
    loadRules();
  }, [tenant?.tenantId]);

  async function loadRules() {
    setLoading(true);
    setError('');
    try {
      const res = await api.listRules();
      setRules(res.rules);
    } catch (err) {
      console.error('Failed to load rules', err);
      setError(err instanceof Error ? err.message : 'Failed to load rules.');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(ruleId: string) {
    setError('');
    try {
      await api.deleteRule(ruleId);
      setRules((prev) => prev.filter((r) => r.Name !== ruleId));
    } catch (err) {
      console.error('Failed to delete rule', err);
      setError(err instanceof Error ? err.message : `Failed to delete rule "${ruleId}".`);
    }
  }

  function openCreate() {
    setEditingRule(undefined);
    setDrawerVisible(true);
  }

  function openEdit(rule: MailManagerRule) {
    setEditingRule(toEditingRule(rule));
    setDrawerVisible(true);
  }

  function closeDrawer() {
    setDrawerVisible(false);
    setEditingRule(undefined);
  }

  if (loading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <>
      {error && (
        <Box padding={{ bottom: 's' }}>
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        </Box>
      )}
      <Table
        header={
          <Header
            variant="h1"
            description="Rules are evaluated top to bottom. The first match wins."
            actions={
              <Button variant="primary" onClick={openCreate}>
                Add Rule
              </Button>
            }
          >
            Routing Rules
          </Header>
        }
        columnDefinitions={[
          {
            id: 'order',
            header: '#',
            cell: (item: MailManagerRule) => rules.indexOf(item) + 1,
            width: 50,
          },
          {
            id: 'name',
            header: 'Rule Name',
            cell: (item: MailManagerRule) => <Box fontWeight="bold">{item.Name}</Box>,
            width: 220,
          },
          {
            id: 'condition',
            header: 'Condition',
            cell: (item: MailManagerRule) => parseCondition(item),
          },
          {
            id: 'action',
            header: 'Action',
            cell: (item: MailManagerRule) => {
              const action = parseAction(item);
              return action.type === 'forward' ? (
                <Badge color="green">→ Forward</Badge>
              ) : (
                <Badge color="red">⊘ Drop</Badge>
              );
            },
            width: 120,
          },
          {
            id: 'destination',
            header: 'Destination',
            cell: (item: MailManagerRule) => parseAction(item).destination || '—',
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item: MailManagerRule) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="edit" variant="icon" ariaLabel="Edit rule" onClick={() => openEdit(item)} />
                <Button
                  iconName="remove"
                  variant="icon"
                  ariaLabel="Delete rule"
                  onClick={() => handleDelete(item.Name)}
                />
              </SpaceBetween>
            ),
            width: 100,
          },
        ]}
        items={rules}
        empty={
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s">
              <Box variant="h3">No rules configured</Box>
              <Box color="text-body-secondary">Add a rule to start routing inbound email automatically.</Box>
              <Button variant="primary" onClick={openCreate}>
                Add Rule
              </Button>
            </SpaceBetween>
          </Box>
        }
      />
      <RuleFormDrawer
        visible={drawerVisible}
        onDismiss={closeDrawer}
        onSaved={loadRules}
        editingRule={editingRule}
      />
    </>
  );
}
